"use client";

import { c as _c } from "react/compiler-runtime";
import { useNetworkStatus } from "@/contexts/NetworkStatusProvider";
import { useEffect, useState, startTransition } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { useTranslations } from "next-intl";

/**
 * Visual indicator shown when network is offline or reconnecting
 */
export function NetworkStatusIndicator() {
  const $ = _c(23);
  const t = useTranslations("System.network");
  const {
    status,
    isOnline,
    timeSinceOffline
  } = useNetworkStatus();
  const [show, setShow] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isOnline) {
    t0 = () => {
      if (!isOnline) {
        startTransition(() => {
          setShow(true);
        });
      } else {
        const timeout = setTimeout(() => {
          startTransition(() => {
            setShow(false);
          });
        }, 2000);
        return () => clearTimeout(timeout);
      }
    };
    t1 = [isOnline];
    $[0] = isOnline;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (!show) {
    return null;
  }
  const formatTime = _temp;
  const t2 = `
          flex items-center gap-2 px-4 py-2 rounded-full shadow-lg
          ${status === "offline" ? "bg-destructive-strong text-white" : status === "reconnecting" ? "bg-amber-700 text-white" : "bg-emerald-700 text-white"}
        `;
  let t3;
  if ($[3] !== status) {
    t3 = status === "offline" ? <WifiOff className="w-4 h-4" /> : <Wifi className="w-4 h-4" />;
    $[3] = status;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== status || $[6] !== t || $[7] !== timeSinceOffline) {
    t4 = status === "offline" && <>{t("offline")}{timeSinceOffline > 5000 && <span className="ml-2">{"("}{formatTime(timeSinceOffline)}{")"}</span>}</>;
    $[5] = status;
    $[6] = t;
    $[7] = timeSinceOffline;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== status || $[10] !== t) {
    t5 = status === "reconnecting" && <>{t("reconnecting")}<span className="inline-block ml-2 animate-pulse">{"\u25CF"}</span></>;
    $[9] = status;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== status || $[13] !== t) {
    t6 = status === "online" && t("connected");
    $[12] = status;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <span className="font-medium text-sm">{t4}{t5}{t6}</span>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t7) {
    t8 = <div className="top-4 left-1/2 z-[9999] fixed slide-in-from-top -translate-x-1/2 animate-in duration-300" role="alert" aria-live="polite"><div className={t2}>{t3}{t7}</div></div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp(ms) {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}